import React from "react";

type TrainWorkflowSnapshot = {
  activeRoute: string;
  trainSubview: string;
  primarySourceStatus: string | null;
  isParsingRide: boolean;
  uploadProgressStage: string | null;
  hydrationLifecycle: string;
};

type TrainWorkflowErrorBoundaryProps = {
  snapshot: TrainWorkflowSnapshot;
  onReturnToTrain: () => void;
  onClearInterruptedActivity: () => void;
  children: React.ReactNode;
};

type TrainWorkflowErrorBoundaryState = {
  error: Error | null;
  stack: string | null;
};

export class TrainWorkflowErrorBoundary extends React.Component<TrainWorkflowErrorBoundaryProps, TrainWorkflowErrorBoundaryState> {
  state: TrainWorkflowErrorBoundaryState = {
    error: null,
    stack: null
  };

  static getDerivedStateFromError(error: Error): TrainWorkflowErrorBoundaryState {
    return {
      error,
      stack: null
    };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    this.setState({
      error,
      stack: info.componentStack ?? null
    });

    console.error("Train workflow render error", {
      message: error.message,
      stack: error.stack ?? null,
      componentStack: info.componentStack,
      activeRoute: this.props.snapshot.activeRoute,
      trainSubview: this.props.snapshot.trainSubview,
      primarySourceStatus: this.props.snapshot.primarySourceStatus,
      isParsingRide: this.props.snapshot.isParsingRide,
      uploadProgressStage: this.props.snapshot.uploadProgressStage,
      hydrationLifecycle: this.props.snapshot.hydrationLifecycle
    });
  }

  private resetBoundary(): void {
    this.setState({ error: null, stack: null });
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <section className="activity-source-tray" role="alert" aria-live="assertive">
        <header className="source-tray-header">
          <div>
            <p className="eyebrow">TRAIN ERROR</p>
            <h2>Johan encountered an error while processing this activity.</h2>
          </div>
        </header>
        <div className="source-group">
          <p className="source-empty">You can retry the render, return to the Train overview, or clear interrupted activity state.</p>
        </div>
        <div className="activity-intake-actions">
          <button type="button" className="ride-analysis-button primary-action" onClick={() => this.resetBoundary()}>Try Again</button>
          <button type="button" className="ride-analysis-button secondary-action" onClick={() => {
            this.props.onReturnToTrain();
            this.resetBoundary();
          }}>Return to Train</button>
          <button type="button" className="ride-analysis-button secondary-action" onClick={() => {
            this.props.onClearInterruptedActivity();
            this.resetBoundary();
          }}>Clear Interrupted Activity</button>
        </div>
        <details className="upload-diagnostics">
          <summary>Technical details</summary>
          <dl>
            <div><dt>Error</dt><dd>{this.state.error.message}</dd></div>
            <div><dt>Route</dt><dd>{this.props.snapshot.activeRoute}</dd></div>
            <div><dt>Train subview</dt><dd>{this.props.snapshot.trainSubview}</dd></div>
            <div><dt>Primary status</dt><dd>{this.props.snapshot.primarySourceStatus ?? "none"}</dd></div>
            <div><dt>Parsing flag</dt><dd>{this.props.snapshot.isParsingRide ? "true" : "false"}</dd></div>
            <div><dt>Upload stage</dt><dd>{this.props.snapshot.uploadProgressStage ?? "none"}</dd></div>
            <div><dt>Hydration</dt><dd>{this.props.snapshot.hydrationLifecycle}</dd></div>
            <div><dt>Component stack</dt><dd><pre>{this.state.stack ?? "Unavailable"}</pre></dd></div>
          </dl>
        </details>
      </section>
    );
  }
}
